/**
 * Shared Pricing Service
 *
 * This service provides a single source of truth for all pricing calculations
 * across the application (client and server). It ensures consistency between:
 * - Quote creation/editing
 * - Quote preview
 * - Template creation/editing
 * - Order creation
 * - Pricing displays
 * - Server-side price locking
 *
 * All calculations are performed in cents (integers) for precision.
 */

import type {
  Service,
  QuoteService,
  CustomPart,
  QuoteProduct,
  QuoteCustomPart,
  Product,
} from "@onsemble/shared";
import type {
  PricingInput,
  PricingResult,
  DiscountInfo,
  PricingBreakdown,
} from "./pricing-types";

export class PricingService {
  /**
   * Calculate comprehensive pricing for a quote/template
   */
  static calculatePricing(input: PricingInput): PricingResult {
    // Calculate individual components
    const laborCost = this.calculateLaborCost(
      input.laborHours || 0,
      input.laborRate || 0,
    );

    const servicesCost = this.calculateServicesCost(
      input.services || [],
      input.quoteServices || [],
    );

    const customPartsCost = this.calculateCustomPartsCost(
      input.customParts || [],
      input.quoteCustomParts || [],
    );

    // Calculate products cost internally (handles both draft and locked quotes)
    // Products with embedded pricing don't need productsData
    const hasProducts = input.products && input.products.length > 0;
    const hasEmbeddedPricing =
      hasProducts &&
      input.products!.some((p: any) => p.postMarkupTotalPrice !== undefined);
    const productPricing =
      hasProducts &&
      (hasEmbeddedPricing ||
        (input.productsData && input.productsData.length > 0))
        ? this.calculateProductPricing(
            input.products!,
            input.productsData || [],
            input.markupPercent || 0,
            input.partsAffectPrice ?? true,
          )
        : { totalCost: 0, products: [] };

    // Calculate custom parts with markup
    const customPartPricing =
      input.quoteCustomParts && input.customParts
        ? this.calculateCustomPartPricing(
            input.quoteCustomParts,
            input.customParts,
            input.markupPercent || 0,
            input.partsAffectPrice ?? true,
          )
        : { totalCost: 0, customParts: [] };

    // Calculate total parts cost (products + custom parts, both with markup already applied)
    const regularProductsCostPremarkup = productPricing.products.reduce(
      (sum, p) => sum + p.preMarkupTotalPrice,
      0,
    );
    const regularProductsCostPostmarkup = productPricing.totalCost;

    const customPartsCostPremarkup = customPartPricing.customParts.reduce(
      (sum, cp) => sum + cp.preMarkupTotalPrice,
      0,
    );
    const customPartsCostPostmarkup = customPartPricing.totalCost;

    const partsCostPremarkup =
      regularProductsCostPremarkup + customPartsCostPremarkup;
    const partsCostPostmarkup =
      regularProductsCostPostmarkup + customPartsCostPostmarkup;

    // Calculate totals
    const subtotalBeforeDiscount =
      laborCost + servicesCost + partsCostPostmarkup;
    const servicesLaborTotal = laborCost + servicesCost;
    const discountAmount = this.calculateDiscountAmount(
      subtotalBeforeDiscount,
      input.discount,
    );
    const subtotalAfterDiscount = subtotalBeforeDiscount - discountAmount;
    const taxAmount = input.taxAmount || 0;
    const finalTotal = subtotalAfterDiscount + taxAmount;

    // Build breakdown for display
    const breakdown = this.buildBreakdown(input, {
      laborCost,
      servicesCost,
      customPartsCost,
      partsCost: partsCostPostmarkup,
      partsCostPremarkup,
      discountAmount,
      taxAmount,
    });

    return {
      laborCost,
      servicesCost,
      customPartsCost,
      partsCost: partsCostPostmarkup,
      partsCostPremarkup,
      partsCostPostmarkup,
      servicesLaborTotal,
      subtotalBeforeDiscount,
      discountAmount,
      subtotalAfterDiscount,
      taxAmount,
      finalTotal,
      breakdown,
    };
  }

  /**
   * Calculate labor cost in cents
   */
  private static calculateLaborCost(hours: number, rateCents: number): number {
    return Math.round(hours * rateCents);
  }

  /**
   * Calculate services cost in cents
   */
  private static calculateServicesCost(
    services: Service[],
    quoteServices: QuoteService[],
  ): number {
    return quoteServices.reduce((sum, quoteService) => {
      const serviceDetails = services.find((s) => s.uid === quoteService.id);
      if (serviceDetails) {
        // Use custom price if set, otherwise use default price - matches custom parts pattern
        const price =
          quoteService.price !== undefined && quoteService.price !== null
            ? quoteService.price
            : serviceDetails.cost || 0;
        return sum + price;
      }
      return sum;
    }, 0);
  }

  /**
   * Calculate custom parts cost in cents
   */
  private static calculateCustomPartsCost(
    customParts: CustomPart[],
    quoteCustomParts: QuoteCustomPart[],
  ): number {
    return quoteCustomParts.reduce((sum, quoteCustomPart) => {
      const customPartDetails = customParts.find(
        (cp) => cp.uid === quoteCustomPart.customPartId,
      );
      if (customPartDetails) {
        // Use custom price if set, otherwise use default price - matches product pattern
        // Handle both null and undefined from OpenAPI types
        const pricePerItem =
          quoteCustomPart.customPrice !== undefined &&
          quoteCustomPart.customPrice !== null
            ? quoteCustomPart.customPrice
            : customPartDetails.cost || 0;
        return sum + Math.round(pricePerItem * quoteCustomPart.quantity);
      }
      return sum;
    }, 0);
  }

  /**
   * Calculate discount amount in cents
   */
  private static calculateDiscountAmount(
    subtotal: number,
    discount?: DiscountInfo,
  ): number {
    if (!discount || discount.value === 0) {
      return 0;
    }

    const subtotalCents = Math.round(subtotal);

    if (discount.type === "flat_amount") {
      // Flat amount discount - cap at subtotal to prevent negative totals
      return Math.min(Math.round(discount.value), subtotalCents);
    } else {
      // Percentage discount - round the result to nearest cent
      const discountAmount = (subtotalCents * discount.value) / 100;
      return Math.round(discountAmount);
    }
  }

  /**
   * Build detailed breakdown for display
   */
  private static buildBreakdown(
    input: PricingInput,
    calculations: {
      laborCost: number;
      servicesCost: number;
      customPartsCost: number;
      partsCost: number;
      partsCostPremarkup: number; // Added: pre-markup parts cost
      discountAmount: number;
      taxAmount: number;
    },
  ): PricingBreakdown {
    const partsAffectPrice = input.partsAffectPrice ?? true;

    // Calculate markup amount
    const markupAmount = partsAffectPrice
      ? Math.round(
          calculations.partsCostPremarkup * ((input.markupPercent || 0) / 100),
        )
      : 0;

    return {
      labor: {
        hours: input.laborHours || 0,
        rate: input.laborRate || 0,
        total: calculations.laborCost,
      },
      parts: {
        subtotal: calculations.partsCostPremarkup,
        markupPercent: input.markupPercent || 0,
        markupAmount,
        total: calculations.partsCost,
        affectsPrice: partsAffectPrice,
      },
      services: (input.quoteServices || []).map((quoteService) => {
        const serviceDetails = (input.services || []).find(
          (s) => s.uid === quoteService.id,
        );
        const unitPrice =
          quoteService.price !== undefined && quoteService.price !== null
            ? quoteService.price
            : serviceDetails?.cost || 0;
        return {
          id: quoteService.id,
          name: quoteService.name || serviceDetails?.title || "Unknown Service",
          price: unitPrice,
        };
      }),
      customParts: (input.quoteCustomParts || []).map((quoteCustomPart) => {
        const customPartDetails = (input.customParts || []).find(
          (cp) => cp.uid === quoteCustomPart.customPartId,
        );
        // Handle both null and undefined from OpenAPI types
        const unitPrice =
          quoteCustomPart.customPrice !== undefined &&
          quoteCustomPart.customPrice !== null
            ? quoteCustomPart.customPrice
            : customPartDetails?.cost || 0;
        const totalPrice = Math.round(unitPrice * quoteCustomPart.quantity);

        return {
          id: quoteCustomPart.customPartId,
          name:
            quoteCustomPart.customName ||
            customPartDetails?.title ||
            `Custom Part ${quoteCustomPart.customPartId}`,
          price: totalPrice,
        };
      }),
      discount: {
        type: input.discount?.type || "percentage",
        value: input.discount?.value || 0,
        description: input.discount?.description || "",
        amount: calculations.discountAmount,
      },
      tax: {
        amount: calculations.taxAmount,
      },
    };
  }

  /**
   * Calculate enhanced product pricing with markup
   * Handles both draft quotes (calculates from live prices) and locked quotes (uses embedded pricing)
   */
  static calculateProductPricing(
    products: QuoteProduct[],
    productsData: Product[],
    markupPercent: number,
    partsAffectPrice: boolean,
  ): {
    products: Array<{
      productId: string;
      quantity: number;
      preMarkupUnitPrice: number; // in cents, before markup
      postMarkupUnitPrice: number; // in cents, after markup
      preMarkupTotalPrice: number; // in cents, before markup
      postMarkupTotalPrice: number; // in cents, after markup
      name: string;
      description: string;
      hajocaProductId: string;
    }>;
    totalCost: number; // in cents
  } {
    // Check if products have embedded pricing (for locked quotes)
    const hasEmbeddedPricing =
      products.length > 0 &&
      products.some((p: any) => p.postMarkupTotalPrice !== undefined);

    if (hasEmbeddedPricing) {
      // Locked quote: use embedded pricing
      const enhancedProducts = products.map((quoteProduct: any) => ({
        productId: quoteProduct.productId,
        quantity: quoteProduct.quantity,
        preMarkupUnitPrice: quoteProduct.priceAtLock || 0,
        postMarkupUnitPrice: quoteProduct.postMarkupUnitPrice || 0,
        preMarkupTotalPrice: Math.round(
          (quoteProduct.priceAtLock || 0) * quoteProduct.quantity,
        ),
        postMarkupTotalPrice: quoteProduct.postMarkupTotalPrice || 0,
        name:
          quoteProduct.description ||
          `Product ${quoteProduct.productId.slice(0, 8)}`,
        description:
          quoteProduct.description || "Product details not available",
        hajocaProductId: quoteProduct.hajocaProductId || "",
      }));

      const totalCost = enhancedProducts.reduce(
        (sum, product) => sum + product.postMarkupTotalPrice,
        0,
      );

      return {
        products: enhancedProducts,
        totalCost: partsAffectPrice ? totalCost : 0,
      };
    }

    // Draft quote: calculate from live product data
    // Convert productsData to Map for O(1) lookups instead of O(n)
    const productsMap = new Map(productsData.map((p) => [p.uid, p]));

    const enhancedProducts = products.map((quoteProduct) => {
      const productData = productsMap.get(quoteProduct.productId);

      // Calculate prices with and without markup
      const baseUnitPrice = productData?.currentPrice || 0; // in cents
      const markupMultiplier = 1 + markupPercent / 100;
      const unitPriceWithMarkup = partsAffectPrice
        ? Math.round(baseUnitPrice * markupMultiplier)
        : baseUnitPrice;

      const baseTotalPrice = baseUnitPrice * quoteProduct.quantity;
      const totalPriceWithMarkup = unitPriceWithMarkup * quoteProduct.quantity;

      return {
        productId: quoteProduct.productId,
        quantity: quoteProduct.quantity,
        preMarkupUnitPrice: baseUnitPrice,
        postMarkupUnitPrice: unitPriceWithMarkup,
        preMarkupTotalPrice: baseTotalPrice,
        postMarkupTotalPrice: totalPriceWithMarkup,
        name:
          productData?.description ||
          `Product ${quoteProduct.productId.slice(0, 8)}`,
        description:
          productData?.description || "Product details not available",
        hajocaProductId: productData?.hajocaProductId || "",
      };
    });

    const totalCost = enhancedProducts.reduce(
      (sum, product) => sum + product.postMarkupTotalPrice,
      0,
    );

    return {
      products: enhancedProducts,
      totalCost,
    };
  }

  /**
   * Calculate enhanced custom part pricing with markup
   */
  static calculateCustomPartPricing(
    quoteCustomParts: Array<{
      customPartId: string;
      customName?: string | null;
      customPrice?: number | null;
      quantity?: number;
    }>,
    customPartsData: CustomPart[],
    markupPercent: number,
    partsAffectPrice: boolean,
  ): {
    customParts: Array<{
      id: string;
      name: string;
      quantity: number;
      preMarkupUnitPrice: number; // in cents, before markup
      postMarkupUnitPrice: number; // in cents, after markup
      preMarkupTotalPrice: number; // in cents, before markup
      postMarkupTotalPrice: number; // in cents, after markup
    }>;
    totalCost: number; // in cents
  } {
    // Convert customPartsData to Map for O(1) lookups instead of O(n)
    const customPartsMap = new Map(customPartsData.map((cp) => [cp.uid, cp]));

    const enhancedCustomParts = quoteCustomParts.map((quoteCustomPart) => {
      const customPartData = customPartsMap.get(quoteCustomPart.customPartId);
      const quantity = quoteCustomPart.quantity || 1;

      // Use custom price from quote if available, otherwise use base cost
      // Handle both null and undefined from OpenAPI types
      const baseUnitPrice =
        quoteCustomPart.customPrice !== undefined &&
        quoteCustomPart.customPrice !== null
          ? quoteCustomPart.customPrice
          : customPartData?.cost || 0; // in cents

      // Calculate price with markup
      const markupMultiplier = 1 + markupPercent / 100;
      const unitPriceWithMarkup = partsAffectPrice
        ? Math.round(baseUnitPrice * markupMultiplier)
        : baseUnitPrice;

      const baseTotalPrice = Math.round(baseUnitPrice * quantity);
      const totalPriceWithMarkup = Math.round(unitPriceWithMarkup * quantity);

      return {
        id: quoteCustomPart.customPartId,
        name:
          quoteCustomPart.customName ||
          customPartData?.title ||
          `Custom Part ${quoteCustomPart.customPartId}`,
        quantity,
        preMarkupUnitPrice: baseUnitPrice,
        postMarkupUnitPrice: unitPriceWithMarkup,
        preMarkupTotalPrice: baseTotalPrice,
        postMarkupTotalPrice: totalPriceWithMarkup,
      };
    });

    const totalCost = enhancedCustomParts.reduce(
      (sum, customPart) => sum + customPart.postMarkupTotalPrice,
      0,
    );

    return {
      customParts: enhancedCustomParts,
      totalCost,
    };
  }

  /**
   * Apply markup to a price (in cents)
   */
  static applyMarkup(priceCents: number, markupPercent: number = 0): number {
    if (markupPercent === 0) return priceCents;
    const markupMultiplier = 1 + markupPercent / 100;
    return Math.round(priceCents * markupMultiplier);
  }

  /**
   * Validate pricing input
   */
  static validatePricingInput(input: PricingInput): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validate labor
    if (input.laborHours !== undefined && input.laborHours < 0) {
      errors.push("Labor hours cannot be negative");
    }
    if (input.laborRate !== undefined && input.laborRate < 0) {
      errors.push("Labor rate cannot be negative");
    }

    // Validate parts
    if (input.markupPercent !== undefined && input.markupPercent < 0) {
      errors.push("Markup percentage cannot be negative");
    }

    // Validate discount
    if (input.discount) {
      if (input.discount.type === "percentage") {
        if (input.discount.value < 0 || input.discount.value > 100) {
          errors.push("Discount percentage must be between 0 and 100");
        }
      } else if (input.discount.type === "flat_amount") {
        if (input.discount.value < 0) {
          errors.push("Discount amount cannot be negative");
        }
      }
    }

    // Validate tax
    if (input.taxAmount !== undefined && input.taxAmount < 0) {
      errors.push("Tax amount cannot be negative");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

// Export convenience functions for backward compatibility
export const calculatePricing =
  PricingService.calculatePricing.bind(PricingService);
export const calculateProductPricing =
  PricingService.calculateProductPricing.bind(PricingService);
export const validatePricingInput =
  PricingService.validatePricingInput.bind(PricingService);
